import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField,
} from '@fluentui/react';
import { AttachmentArt } from 'formComponents/lookups/AttachmentArt';
import { formStyles } from 'styles/formStyles';
import moment from 'moment-timezone';
import { Attachment, uploadAttachment, SmRefType as SmRefTypeEnum } from 'api/attachment';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SmRefType } from 'formComponents/lookups/SmRefType';
import { SmRefTypeLabel } from 'utils/labels';

const styles = mergeStyleSets({
    dropzone: {
        width: '100%',
        paddingTop: 50,
        paddingBottom: 50,
        border: '3px dashed #CCC',
        textAlign: 'center',
        backgroundColor: '#EEE',
        color: '#BBB',
        fontSize: 16,
        fontWeight: 700,
        cursor: 'pointer',
    },
    fileList: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '1fr 170px 120px 100px max-content',
        gridColumnGap: 10,
    },
});

interface AddAttachmentsProps {
    kNr: number;
    forcedSmRefType?: SmRefTypeEnum;
    forcedSmRef?: string;
    onClose: (refetch: boolean) => void;
}

export const AddAttachments: React.FC<AddAttachmentsProps> = ({ kNr, forcedSmRefType, forcedSmRef, onClose }) => {
    const [addAttachmentArt, setAddAttachmentArt] = React.useState<{ [key: string]: number }>({});
    const [addAttachmentSmRefType, setAddAttachmentSmRefType] = React.useState<{ [key: string]: string }>({});
    const [addAttachmentSmRef, setAddAttachmentSmRef] = React.useState<{ [key: string]: string }>({});
    const [addAttachmentText, setAddAttachmentText] = React.useState<{ [key: string]: string }>({});
    const [addAttachmentAnzeige, setAddAttachmentAnzeige] = React.useState<{ [key: string]: boolean }>({});

    const [saving, setSaving] = React.useState<number | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const { acceptedFiles, getRootProps, getInputProps } = useDropzone({ disabled: saving !== null });

    const saveAttachments = async () => {
        const datum = moment().toISOString();

        if (kNr) {
            let uploadError = false;
            for (let i = 0; i < acceptedFiles.length; i += 1) {
                setSaving(i);
                setError(null);
                const attachment: Attachment = {
                    kNr,
                    datum,
                    art: addAttachmentArt[acceptedFiles[i].name],
                    smRefType: forcedSmRefType || (addAttachmentSmRefType[acceptedFiles[i].name] as SmRefTypeEnum),
                    smRef: forcedSmRef || addAttachmentSmRef[acceptedFiles[i].name],
                    text: addAttachmentText[acceptedFiles[i].name],
                    file: '',
                    anzeige: addAttachmentAnzeige[acceptedFiles[i].name],
                    oneDriveUrl: null,
                };

                // eslint-disable-next-line no-await-in-loop
                const result = await uploadAttachment(acceptedFiles[i], attachment);
                if (!result) {
                    uploadError = true;
                    setError('Fehler beim Speichern von ' + acceptedFiles[i].name);
                    break;
                }
            }

            setSaving(null);

            if (!uploadError) {
                onClose(true);
            }
        }
    };

    return (
        <Dialog
            hidden={false}
            title="Dokumente anhängen"
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
                layerProps: { eventBubblingEnabled: true },
                dragOptions: {
                    moveMenuItemText: 'Move',
                    closeMenuItemText: 'Close',
                    menu: ContextualMenu,
                },
            }}
            minWidth={1000}
            onDismiss={() => onClose(false)}
        >
            <div {...getRootProps({ className: styles.dropzone })}>
                <input {...getInputProps()} />
                <p>
                    Ziehen Sie mit der Maus eine oder mehrere Dateien in diesen Bereich.
                    <br />
                    Alternativ können Sie auch in diesen Bereich klicken und die Dateien auswählen.
                </p>
            </div>
            <div className={styles.fileList}>
                {acceptedFiles.map((file) => {
                    return (
                        <React.Fragment key={file.name}>
                            <TextField
                                label={'Beschreibung - ' + file.name}
                                value={addAttachmentText[file.name]}
                                disabled={saving !== null}
                                maxLength={100}
                                onChange={(event, newValue) => {
                                    const newTexts = {
                                        ...addAttachmentText,
                                        [file.name]: newValue || '',
                                    };

                                    setAddAttachmentText(newTexts);
                                }}
                            />
                            <div>
                                <AttachmentArt
                                    value={addAttachmentArt[file.name]?.toString() || '3'}
                                    disabled={saving !== null}
                                    menuPlacement="top"
                                    onChange={(newValue): void => {
                                        const newArtList = {
                                            ...addAttachmentArt,
                                            [file.name]: parseInt(newValue, 10),
                                        };

                                        setAddAttachmentArt(newArtList);
                                    }}
                                />
                            </div>
                            <div>
                                <SmRefType
                                    label="Verbunden mit..."
                                    value={forcedSmRefType || addAttachmentSmRefType[file.name] || ''}
                                    disabled={saving !== null || !!forcedSmRefType}
                                    menuPlacement="top"
                                    onChange={(newValue): void => {
                                        const newSmRefTypeList = {
                                            ...addAttachmentSmRefType,
                                            [file.name]: newValue,
                                        };

                                        setAddAttachmentSmRefType(newSmRefTypeList);
                                    }}
                                />
                            </div>
                            <TextField
                                label={(SmRefTypeLabel[addAttachmentSmRefType[file.name]] || '?') + '-Nr.'}
                                value={forcedSmRef || addAttachmentSmRef[file.name] || ''}
                                disabled={saving !== null || !!forcedSmRef}
                                maxLength={100}
                                onChange={(event, newValue) => {
                                    const newTexts = {
                                        ...addAttachmentSmRef,
                                        [file.name]: newValue || '',
                                    };

                                    setAddAttachmentSmRef(newTexts);
                                }}
                            />
                            <div>
                                <Checkbox
                                    className={formStyles.checkboxInsideFormAligned}
                                    label="Für AD sichtbar"
                                    disabled={saving !== null}
                                    checked={addAttachmentAnzeige[file.name]}
                                    onChange={(event, checked) => {
                                        const newAnzeigeList = {
                                            ...addAttachmentAnzeige,
                                            [file.name]: !!checked,
                                        };

                                        setAddAttachmentAnzeige(newAnzeigeList);
                                    }}
                                />
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            {error && <div className={formStyles.error}>{error}</div>}

            <DialogFooter>
                <DefaultButton disabled={saving !== null} onClick={() => onClose(false)} text="Abbrechen" />
                <PrimaryButton
                    disabled={acceptedFiles.length === 0 || saving !== null}
                    onClick={saveAttachments}
                    text="Speichern"
                />
            </DialogFooter>
        </Dialog>
    );
};
